import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components/Box';
import { Column } from '@/components/lib/components/Column';
import { ds } from '@/components/lib/design.system';
import { Stake } from '@/feature/lobby-arcade/hooks/use-arcade-games';
import { triggerHapticFeedback } from '@/utils/haptic-feedback';
import { toLocaleCurrency } from '@/utils/numeric/currency';

import { useGameSettingsStore } from '../hooks/use-game-settings';
import { RadialSlider } from './RadialSlider/RadialSlider';

type EntryRadialSliderProps = {
    stakes: Stake[];
};

const CenterContent = ({ selectedEntry }: { selectedEntry: Stake }) => {
    const { t } = useTranslation();
    return (
        <Box alignItems="center" justifyContent="center">
            <Text variant="titleMedium2" color={ds.colorToken.text.primary}>
                {t('to_win')}
            </Text>
            <Text fontSize={56} fontWeight={700} lineHeight={66} color={ds.colorToken.text.primary}>
                {toLocaleCurrency(selectedEntry.stake * 2 - selectedEntry.rake * 2)}
            </Text>
        </Box>
    );
};

const TopContent = ({ selectedEntry }: { selectedEntry: Stake }) => {
    const { t } = useTranslation();
    return (
        <Column>
            <Text variant="titleMedium2" color={ds.colorToken.text.primary}>
                <Text variant="titleMedium2" color={ds.colorToken.text.brand}>
                    {toLocaleCurrency(selectedEntry.stake)}{' '}
                </Text>
                {t('entry')}
            </Text>
        </Column>
    );
};

// entry options include a default "free" entry option at index 0 (which is disabled), so we start the first valid entry at index 1
const FIRST_VALID_ENTRY_INDEX = 1;

export const EntryRadialSlider = ({ stakes }: EntryRadialSliderProps) => {
    const selectedStake = useGameSettingsStore(state => state.selectedStake);
    const setGameSettings = useGameSettingsStore(state => state.actions.setGameSettings);

    const [selectedIndex, setSelectedIndex] = useState(() => {
        const index = stakes.findIndex(
            stake => stake.stake === selectedStake?.stake && stake.currency === selectedStake?.currency
        );
        return index > 0 ? index : FIRST_VALID_ENTRY_INDEX;
    });

    const hasValidEntries = stakes.length > FIRST_VALID_ENTRY_INDEX;
    const selectedEntry = stakes[selectedIndex] ?? stakes[stakes.length - 1];

    const handleValueChange = useCallback((value: number) => {
        setSelectedIndex(currentIndex => {
            if (value !== currentIndex) {
                triggerHapticFeedback();
                return value;
            }
            return currentIndex;
        });
    }, []);

    useEffect(() => {
        if (hasValidEntries) {
            setGameSettings({ selectedStake: selectedEntry });
        }
    }, [hasValidEntries, selectedEntry, setGameSettings]);

    if (!hasValidEntries) {
        return null;
    }

    return (
        <RadialSlider
            centerContent={<CenterContent selectedEntry={selectedEntry} />}
            topContent={<TopContent selectedEntry={selectedEntry} />}
            onValueChange={handleValueChange}
            max={stakes.length - 1}
            initialValue={selectedIndex}
            minAllowable={1}
        />
    );
};
